import { MessageCircle, Send } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import {
  useChannelInbox,
  useChannelThread,
  useSendChannelReply,
} from '~/entities/channel/model/use-channel'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { cn } from '~/shared/lib/utils'
import { Badge } from '~/shared/ui/badge'
import { Button } from '~/shared/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/shared/ui/card'
import { Textarea } from '~/shared/ui/textarea'

const formatTime = (iso: string) =>
  new Date(iso).toLocaleString(getLocale(), { dateStyle: 'short', timeStyle: 'short' })

export function ChannelInbox() {
  const inbox = useChannelInbox()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const thread = useChannelThread(selectedId)
  const reply = useSendChannelReply(selectedId)
  const [draft, setDraft] = useState('')
  const scrollRef = useRef<HTMLDivElement | null>(null)

  const conversations = inbox.data ?? []
  const messages = thread.data?.messages ?? []

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight })
  }, [messages.length])

  const handleReply = () => {
    const message = draft.trim()
    if (!message || reply.isPending) return
    reply.mutate(message, { onSuccess: () => setDraft('') })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageCircle className="w-4 h-4" />
          {m.channelInboxTitle()}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {conversations.length === 0 ? (
          <p className="px-6 pb-6 text-slate-500 dark:text-slate-400 text-sm">
            {m.channelInboxEmpty()}
          </p>
        ) : (
          <div className="grid sm:grid-cols-[280px_1fr] border-slate-200 dark:border-slate-800 border-t">
            <div className="border-slate-200 dark:border-slate-800 sm:border-r max-h-[480px] overflow-y-auto">
              {conversations.map((conversation) => (
                <button
                  key={conversation.conversationId}
                  type="button"
                  className={cn(
                    'flex w-full flex-col gap-1 border-b border-slate-100 px-4 py-3 text-left dark:border-slate-900',
                    selectedId === conversation.conversationId
                      ? 'bg-slate-100 dark:bg-slate-900'
                      : 'hover:bg-slate-50 dark:hover:bg-slate-900/50',
                  )}
                  onClick={() => setSelectedId(conversation.conversationId)}
                >
                  <span className="flex items-center gap-2">
                    <span className="flex-1 font-medium text-slate-900 dark:text-slate-100 text-sm truncate">
                      {conversation.user.displayName}
                    </span>
                    {conversation.unreadCount > 0 && (
                      <Badge className="bg-blue-600 text-white">
                        {m.channelInboxUnread({ count: conversation.unreadCount })}
                      </Badge>
                    )}
                  </span>
                  <span className="text-slate-500 dark:text-slate-400 text-xs truncate">
                    {conversation.lastMessageBody ?? '-'}
                  </span>
                  <span className="text-slate-400 dark:text-slate-500 text-xs">
                    {formatTime(conversation.lastMessageAt)}
                  </span>
                </button>
              ))}
            </div>
            <div className="flex flex-col min-h-[320px]">
              {selectedId == null ? (
                <p className="place-content-center grid flex-1 p-6 text-slate-500 dark:text-slate-400 text-sm text-center">
                  {m.channelInboxSelect()}
                </p>
              ) : (
                <>
                  <div
                    ref={scrollRef}
                    className="flex-1 space-y-2 p-4 max-h-[380px] overflow-y-auto"
                  >
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={cn('flex', message.fromSystem ? 'justify-end' : 'justify-start')}
                      >
                        <div
                          className={cn(
                            'max-w-[75%] rounded-lg px-3 py-2 text-sm',
                            message.fromSystem
                              ? 'bg-blue-600 text-white'
                              : 'bg-slate-100 text-slate-900 dark:bg-slate-900 dark:text-slate-100',
                          )}
                        >
                          <p className="break-words whitespace-pre-wrap">{message.body ?? '-'}</p>
                          <p
                            className={cn(
                              'mt-1 text-xs',
                              message.fromSystem
                                ? 'text-blue-100'
                                : 'text-slate-400 dark:text-slate-500',
                            )}
                          >
                            {formatTime(message.createdAt)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2 p-4 border-slate-200 dark:border-slate-800 border-t">
                    <Textarea
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      placeholder={m.channelReplyPlaceholder()}
                      rows={2}
                      maxLength={2000}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault()
                          handleReply()
                        }
                      }}
                    />
                    <Button
                      className="self-end bg-blue-600 hover:bg-blue-700 text-white"
                      disabled={draft.trim().length === 0 || reply.isPending}
                      onClick={handleReply}
                    >
                      <Send className="mr-2 w-4 h-4" />
                      {m.channelReplySend()}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
